import { useMemo, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { CardElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import { ShieldCheck } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { formatUsdFromCents } from '@/components/billing/mapleosProducts';
import { cn } from '@/lib/utils';

const STRIPE_PUBLISHABLE_KEY = 'pk_live_51J1PlkHhjncHjxc7Wmua97kzLw2r8VUGPO8i8iXhU9lMR0yVsNkRY4BoHDRQUGDN54QVjK0be7ioaaVD8G5ghX5D00O5DiqLdA';
const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY, { advancedFraudSignals: false });

type AICreditEmbeddedPaymentProps = {
  amountCents: number;
  savePaymentMethod: boolean;
  dark?: boolean;
  disabled?: boolean;
  onSuccess?: () => Promise<unknown> | unknown;
};

function EmbeddedPaymentForm({ amountCents, savePaymentMethod, dark = false, disabled = false, onSuccess }: AICreditEmbeddedPaymentProps) {
  const stripe = useStripe();
  const elements = useElements();
  const { user, session } = useAuth();
  const [submitting, setSubmitting] = useState(false);

  const cardElementOptions = useMemo(
    () => ({
      style: {
        base: {
          color: dark ? '#F8FAFC' : '#111827',
          fontSize: '16px',
          iconColor: dark ? '#CBD5E1' : '#64748B',
          '::placeholder': {
            color: dark ? '#94A3B8' : '#6B7280',
          },
        },
        invalid: {
          color: '#DC2626',
          iconColor: '#DC2626',
        },
      },
      hidePostalCode: true,
    }),
    [dark]
  );

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!user || !session?.access_token) {
      toast({
        title: 'Sign in required',
        description: 'Please sign in before buying more AI credits.',
        variant: 'destructive',
      });
      return;
    }

    if (amountCents < 500) {
      toast({
        title: 'Minimum top-up is $5',
        description: 'Enter at least $5 to buy more AI credits.',
        variant: 'destructive',
      });
      return;
    }

    if (!stripe || !elements) {
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      toast({
        title: 'Payment form not ready',
        description: 'Please wait a moment and try again.',
        variant: 'destructive',
      });
      return;
    }

    setSubmitting(true);

    try {
      const { data, error } = await supabase.functions.invoke('create-ai-credit-topup-intent', {
        body: {
          amountCents,
          savePaymentMethod,
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        throw error;
      }

      if (!data?.clientSecret || !data?.paymentIntentId) {
        throw new Error('Unable to initialize secure payment.');
      }

      const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(data.clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            email: user.email || undefined,
          },
        },
      });

      if (confirmError) {
        throw confirmError;
      }

      if (!paymentIntent?.id || paymentIntent.status !== 'succeeded') {
        throw new Error('Payment is still processing. Please try again in a moment.');
      }

      const { error: finalizeError } = await supabase.functions.invoke('finalize-ai-credit-topup-intent', {
        body: {
          paymentIntentId: paymentIntent.id,
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (finalizeError) {
        throw finalizeError;
      }

      window.dispatchEvent(new CustomEvent('mapleos:credits-refresh'));
      toast({
        title: 'AI credits added',
        description: `${amountCents.toLocaleString()} credits are now available.`,
      });
      await onSuccess?.();
    } catch (error: any) {
      toast({
        title: 'Top-up failed',
        description: error?.message || 'Please check your payment details and try again.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className={cn('rounded-xl border p-4', dark ? 'border-white/10 bg-white/[0.03]' : 'border-border bg-muted/20')}>
        <div className="mb-2 flex items-center gap-2 text-sm font-medium">
          <ShieldCheck className="h-4 w-4" />
          Secure card payment
        </div>
        <div className={cn('rounded-md border px-3 py-3', dark ? 'border-white/10 bg-black/20' : 'border-border bg-background')}>
          <CardElement options={cardElementOptions} />
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          You will be charged {formatUsdFromCents(amountCents)} and receive {amountCents.toLocaleString()} MapleOS AI credits.
        </p>
      </div>

      <Button type="submit" className="w-full" variant="outline" disabled={disabled || submitting || !stripe || amountCents < 500}>
        {submitting ? 'Processing payment...' : `Pay ${formatUsdFromCents(amountCents)}`}
      </Button>
    </form>
  );
}

export default function AICreditEmbeddedPayment(props: AICreditEmbeddedPaymentProps) {
  return (
    <Elements stripe={stripePromise}>
      <EmbeddedPaymentForm {...props} />
    </Elements>
  );
}